import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { sentimentAnalysisSchema } from '@constants';
import {
  CreateSentimentPayload,
  FeedbackItem,
  SENTIMENT_ANALYSIS_OPTION,
  SENTIMENT_ANALYSIS_SUBCATEGORIES,
  UpdateSentimentPayload,
} from 'src/features/Enterprise/SentimentAnalysis/types';
import MainModal from './MainModal';
import { useSentimentAnalysis } from '../useSentimentAnalysis';

interface AddSentimentAnalysisModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  categories: SENTIMENT_ANALYSIS_OPTION[];
  subCategories: SENTIMENT_ANALYSIS_SUBCATEGORIES[];
  editingItem?: FeedbackItem;
  onClose: () => void;
}

export const AddSentimentAnalysisModal: FC<AddSentimentAnalysisModalProps> = ({
  onClose,
  open,
  title,
  subtitle,
  categories,
  subCategories,
  editingItem,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<FeedbackItem>({
    resolver: yupResolver(sentimentAnalysisSchema),
    defaultValues: editingItem,
    mode: 'onChange',
    delayError: 700,
  });

  const isEdit = !!editingItem;

  const { isCreating, isEditing, createSentiment, editSentiment } = useSentimentAnalysis();

  const handleCloseAndReset = () => {
    reset();
    onClose();
  };

  const onFinalSubmit = (data: FeedbackItem) => {
    if (data) {
      isEdit
        ? editSentiment({ ...data, uuids: [editingItem.id] } as unknown as UpdateSentimentPayload, {
            onSuccess: () => handleCloseAndReset(),
          })
        : createSentiment(data as unknown as CreateSentimentPayload, {
            onSuccess: () => handleCloseAndReset(),
          });
    }
  };

  useEffect(() => {
    if (open) {
      reset(editingItem);
    }
  }, [editingItem, open, reset]);

  return (
    <>
      <MainModal
        isEdit={isEdit}
        open={open}
        isValid={isValid}
        isLoading={isCreating || isEditing}
        control={control}
        handleSubmit={handleSubmit}
        onClose={handleCloseAndReset}
        onFinalSubmit={onFinalSubmit}
        title={title}
        subtitle={subtitle}
        categories={categories}
        subCategories={subCategories}
      />
    </>
  );
};
